import React, { useEffect, useMemo, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { toLocalDateString } from '../../utils/dateUtils';

export interface HorizontalDateStripProps {
  /** Selected date (YYYY-MM-DD) */
  selectedDate: string;
  /** Called when a day is selected (YYYY-MM-DD) */
  onSelectDate: (dateStr: string) => void;
  /**
   * Inclusive range of dates covered by the current view (YYYY-MM-DD).
   * Days inside it render active, so a 7/30-day window reads as a block
   * on the strip rather than a single anchor day.
   */
  rangeStart?: string;
  rangeEnd?: string;
  /** Number of events on a date — rendered as dots (capped at 4) */
  eventCountForDate?: (dateStr: string) => number;
  /** Days of history before today (default 30) */
  daysBack?: number;
  /** Days of future after today (default 180) */
  daysForward?: number;
  className?: string;
}

type StripItem =
  | { kind: 'month'; label: string; key: string }
  | {
      kind: 'day';
      dateStr: string;
      dayNum: number;
      weekday: string;
      isToday: boolean;
      isWeekend: boolean;
    };

const MAX_DOTS = 4;

/**
 * Horizontally scrolling date selector: one card per day (weekday, day number,
 * event dots), with month labels at month boundaries. Replaces the sidebar
 * mini-month calendar on the Legislative Calendar so the event list gets the
 * full page width. The strip window is anchored to today and widened when the
 * selected date (e.g. from the URL) falls outside it.
 */
const HorizontalDateStrip: React.FC<HorizontalDateStripProps> = ({
  selectedDate,
  onSelectDate,
  rangeStart,
  rangeEnd,
  eventCountForDate = () => 0,
  daysBack = 30,
  daysForward = 180,
  className = '',
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const firstScrollDone = useRef(false);

  const todayStr = toLocalDateString(new Date());

  const items = useMemo<StripItem[]>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start = new Date(today);
    start.setDate(start.getDate() - daysBack);
    const end = new Date(today);
    end.setDate(end.getDate() + daysForward);

    // Widen the window if the selected date lies outside it
    if (selectedDate) {
      const [y, m, d] = selectedDate.split('-').map((v) => parseInt(v, 10));
      if (y && m && d) {
        const sel = new Date(y, m - 1, d);
        if (sel < start) start.setTime(new Date(y, m - 1, d - 7).getTime());
        if (sel > end) end.setTime(new Date(y, m - 1, d + 7).getTime());
      }
    }

    const out: StripItem[] = [];
    const cursor = new Date(start);
    while (cursor <= end) {
      if (cursor.getDate() === 1 || cursor.getTime() === start.getTime()) {
        const label = cursor.toLocaleDateString('en-US', { month: 'short' });
        out.push({ kind: 'month', label, key: `m-${cursor.getFullYear()}-${cursor.getMonth()}` });
      }
      const dateStr = toLocalDateString(cursor);
      const dow = cursor.getDay();
      out.push({
        kind: 'day',
        dateStr,
        dayNum: cursor.getDate(),
        weekday: cursor.toLocaleDateString('en-US', { weekday: 'short' }),
        isToday: dateStr === todayStr,
        isWeekend: dow === 0 || dow === 6,
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    return out;
  }, [selectedDate, daysBack, daysForward, todayStr]);

  // Keep the selected day in view; jump on first render, glide afterwards
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const target = container.querySelector<HTMLElement>(`[data-date="${selectedDate}"]`);
    if (!target) return;
    const left = target.offsetLeft - container.clientWidth / 2 + target.clientWidth / 2;
    container.scrollTo({ left, behavior: firstScrollDone.current ? 'smooth' : 'auto' });
    firstScrollDone.current = true;
  }, [selectedDate, items]);

  const scrollByPage = (direction: -1 | 1) => {
    const container = scrollRef.current;
    if (!container) return;
    container.scrollBy({ left: direction * container.clientWidth * 0.8, behavior: 'smooth' });
  };

  return (
    <div className={`flex items-stretch gap-1 ${className}`}>
      <button
        type="button"
        onClick={() => scrollByPage(-1)}
        className="flex-shrink-0 self-center p-1.5 rounded-lg hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
        aria-label="Scroll to earlier dates"
      >
        <ChevronLeft className="w-5 h-5 text-text-secondary dark:text-text-dark-secondary" />
      </button>

      <div
        ref={scrollRef}
        className="flex-1 min-w-0 flex items-stretch gap-1.5 overflow-x-auto py-1 scrollbar-hide"
        role="listbox"
        aria-label="Select a date"
      >
        {items.map((item) => {
          if (item.kind === 'month') {
            return (
              <div
                key={item.key}
                className="flex-shrink-0 w-5 flex items-center justify-center"
                aria-hidden="true"
              >
                <span className="text-[10px] font-semibold uppercase tracking-wide text-text-tertiary dark:text-text-dark-tertiary [writing-mode:vertical-rl] rotate-180">
                  {item.label}
                </span>
              </div>
            );
          }

          const isSelected = item.dateStr === selectedDate;
          const inRange =
            !isSelected &&
            !!rangeStart &&
            !!rangeEnd &&
            item.dateStr >= rangeStart &&
            item.dateStr <= rangeEnd;
          const dotCount = Math.min(eventCountForDate(item.dateStr), MAX_DOTS);

          // Today keeps a strong border in every non-selected state so it stays
          // findable even inside an active 7/30-day range.
          const todayBorder = item.isToday
            ? 'border-primary-600 dark:border-primary-400'
            : '';

          const stateClasses = isSelected
            ? 'bg-primary-600 border-primary-600 text-white shadow-sm'
            : inRange
              ? `bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-900/50 ${todayBorder || 'border-primary-200 dark:border-primary-800'}`
              : item.isToday
                ? `bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/40 ${todayBorder}`
                : `${item.isWeekend ? 'bg-background-secondary dark:bg-background-dark-tertiary' : 'bg-white dark:bg-background-dark-secondary'} border-border-light dark:border-border-dark text-text-secondary dark:text-text-dark-secondary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary`;

          return (
            <button
              key={item.dateStr}
              type="button"
              data-date={item.dateStr}
              onClick={() => onSelectDate(item.dateStr)}
              role="option"
              aria-selected={isSelected}
              aria-current={item.isToday ? 'date' : undefined}
              aria-label={`Select ${item.dateStr}`}
              className={`flex-shrink-0 w-12 py-1.5 rounded-lg border flex flex-col items-center gap-0.5 transition-colors select-none ${stateClasses}`}
            >
              <span className="text-[10px] font-semibold uppercase tracking-wide">
                {item.weekday}
              </span>
              <span
                className={`text-base font-bold leading-tight ${
                  isSelected
                    ? ''
                    : 'text-text-primary dark:text-text-dark-primary'
                }`}
              >
                {item.dayNum}
              </span>
              <span className="flex items-center gap-[3px] h-1.5" aria-hidden="true">
                {Array.from({ length: dotCount }, (_, i) => (
                  <span
                    key={i}
                    className={`w-1 h-1 rounded-full ${
                      isSelected ? 'bg-white/90' : 'bg-primary-600 dark:bg-primary-400'
                    }`}
                  />
                ))}
              </span>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => scrollByPage(1)}
        className="flex-shrink-0 self-center p-1.5 rounded-lg hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
        aria-label="Scroll to later dates"
      >
        <ChevronRight className="w-5 h-5 text-text-secondary dark:text-text-dark-secondary" />
      </button>
    </div>
  );
};

export default HorizontalDateStrip;
